import { DownloadSimpleIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import * as Yup from 'yup';

import { BaseButton } from '../../../components/button/BaseButton';
import { Button } from '../../../components/button/Button';
import { Dialog } from '../../../components/dialog/Dialog';
import { SelectWithLabel } from '../../../components/select/SelectWithLabel';
import { exportInvoices } from '../models';
import { Formik } from 'components/form/formik';
import { InputField } from '../../../components/input/InputField';
import { getDisplayError } from '../../../utils/get-display-error';
import { downloadTextFile } from '../../../utils/download';
import { parseNumberInput } from '../../../utils/number';
import { captureException } from '@sentry/react';

const MONTH_NAMES = [
  'Januari',
  'Februari',
  'Maart',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Augustus',
  'September',
  'Oktober',
  'November',
  'December',
];

function getMonthOptions(): { value: string; label: string; month: number }[] {
  const now = new Date();
  const options: { value: string; label: string; month: number }[] = [];

  for (let i = -3; i <= 1; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const yy = String(d.getFullYear()).slice(-2);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yearMonth = `${yy}${mm}`;
    const label = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()} (VK${mm})`;
    options.push({ value: yearMonth, label, month: d.getMonth() + 1 });
  }

  return options;
}

function getCurrentYearMonth(): string {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  return `${yy}${mm}`;
}

const exportInvoicesSchema = Yup.object().shape({
  startId: Yup.string().min(1, 'Vereist').required('Vereist'),
  endId: Yup.string().min(1, 'Vereist').required('Vereist'),
  financialPeriod: Yup.string().min(1, 'Vereist').required('Vereist'),
});

export const ExportInvoicesButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const monthOptions = getMonthOptions();
  const currentYearMonth = getCurrentYearMonth();

  return (
    <>
      <BaseButton
        color="default"
        iconLeft={<DownloadSimpleIcon className="button-icon" />}
        isLoading={isLoading}
        disabled={isLoading}
        onClick={() => setOpen(true)}
      >
        Exporteer verkopen
      </BaseButton>

      <Dialog isOpen={open} onClose={() => setOpen(false)} title="Exporteer verkopen">
        <Formik
          initialValues={{
            yearMonth: currentYearMonth,
            startId: `TD-${currentYearMonth}00001`,
            endId: `TD-${currentYearMonth}99999`,
            financialPeriod: String(new Date().getMonth() + 1),
          }}
          validationSchema={exportInvoicesSchema}
          onSubmit={async ({ startId, endId, financialPeriod }) => {
            setIsLoading(true);
            try {
              const result = await exportInvoices({
                startId,
                endId,
                financialPeriod: parseNumberInput(financialPeriod),
              });

              downloadTextFile(result.customers, `customers-${startId}-${endId}.xml`, 'text/xml');
              downloadTextFile(result.invoices, `invoices-${startId}-${endId}.xml`, 'text/xml');

              toast.success('Factuur export download gestart');
            } catch (err) {
              captureException(err);
              console.error(err);
              toast.error('Kon verkopen niet exporteren: ' + getDisplayError(err), {
                id: 'export-sales-error',
              });
            } finally {
              setIsLoading(false);
            }
          }}
        >
          {({ handleSubmit, isSubmitting, setFieldValue, values }) => {
            const selectedItem = monthOptions.find((o) => o.value === values.yearMonth) ?? null;

            return (
              <form onSubmit={handleSubmit}>
                <SelectWithLabel
                  labelText="Periode"
                  name="Selecteer een periode..."
                  items={monthOptions}
                  selectedItem={selectedItem}
                  keyName="value"
                  display={(item) => item.label}
                  onSelect={(opt) => {
                    if (!opt) return;
                    setFieldValue('yearMonth', opt.value);
                    setFieldValue('startId', `TD-${opt.value}00001`);
                    setFieldValue('endId', `TD-${opt.value}99999`);
                    setFieldValue('financialPeriod', String(opt.month));
                  }}
                />

                <InputField labelText="Start nummer" type="text" name="startId" />
                <InputField labelText="Eind nummer" type="text" name="endId" />
                <InputField labelText="Financiele Periode" type="number" name="financialPeriod" />

                <div className="mt-8">
                  <Button
                    type="submit"
                    color="primary"
                    isDisabled={isSubmitting}
                    isLoading={isSubmitting}
                    iconLeft={<DownloadSimpleIcon className="button-icon" />}
                  >
                    Exporteer verkopen
                  </Button>
                </div>
              </form>
            );
          }}
        </Formik>
      </Dialog>
    </>
  );
};
